import useDebouncedStateString from 'hooks/useDebouncedState';
import InputTextField, { IInputTextFieldProps, ILabelOptions } from './InputTextField';
import IModel from 'models/model';

interface IntegerFieldProps<T extends IModel> extends IInputTextFieldProps<T> {
  labelOptions?: ILabelOptions;
  value?: any;
  state?: T;
  setState?: React.Dispatch<React.SetStateAction<T>>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const IntegerField = <T extends IModel>({
  inputName,
  state,
  value,
  setState,
  onChange,
  ...props
}: IntegerFieldProps<T>) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!/^-?\d*$/.test(value)) return;

    onChange?.(event);
  };

  return (
    <InputTextField
      {...props}
      inputName={inputName}
      state={state}
      inputProps={{
        inputMode: 'numeric',
        ...props.inputProps,
      }}
      setState={setState}
      value={value}
      onChange={handleChange}
    />
  );
};

interface IntegerFieldDebouncedProps<T extends IModel> extends IntegerFieldProps<T> {
  labelOptions?: ILabelOptions;
  state?: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
}

export const IntegerFieldDebounced = <T extends IModel>({
  inputName,
  state,
  setState,
  ...props
}: IntegerFieldDebouncedProps<T>) => {
  const [value, onValueChange] = useDebouncedStateString(String(state?.[inputName] ?? ''), setState, inputName);

  return (
    <IntegerField
      {...props}
      inputName={inputName}
      state={state}
      setState={setState}
      value={value}
      onChange={event => onValueChange(event.target.value)}
    />
  );
};

export default IntegerField;
